import { z } from 'zod'

export const tokenResponseSchema = z.object({
  token: z.string().min(1),
})

export const messageResponseSchema = z.object({
  message: z.string(),
})

export const oauthStartResponseSchema = z.object({
  url: z.url(),
})

export const authMethodSchema = z.object({
  name: z.string().min(1),
  url: z.url(),
  image_path: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
})

export const authMethodsResponseSchema = z.object({
  methods: z.array(authMethodSchema),
  notices: z.array(
    z.object({
      code: z.string(),
      message: z.string(),
    }),
  ),
})

const settingsSchema = z
  .object({
    default_review_mode: z.enum(['free', 'scheduled']).optional(),
    ppr_session_card_limit: z.number().int().min(20).max(9999).optional(),
    theme: z.enum(['light', 'dark']).optional(),
  })
  .catchall(z.unknown())

export const currentUserSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  surname: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  email: z
    .email()
    .nullish()
    .transform((value) => value ?? null),
  created_at: z.string(),
  gender: z
    .enum(['male', 'female'])
    .nullish()
    .transform((value) => value ?? null),
  birthday_date: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  image_path: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  lang: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  settings: settingsSchema,
  default_category_id: z
    .number()
    .int()
    .positive()
    .nullish()
    .transform((value) => value ?? null),
  is_partner: z
    .boolean()
    .nullish()
    .transform((value) => value ?? null),
  subscription: z.object({
    type: z.string(),
    till: z
      .string()
      .nullish()
      .transform((value) => value ?? null),
    promocode: z.string().nullable().optional(),
    indefinite: z.boolean().optional(),
  }),
  description: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
})

export const publicUserSchema = z.object({
  description: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  email: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  id: z.number().int().positive(),
  image_path: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  name: z.string(),
  surname: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
})

const studyStatsSchema = z.object({
  card_groups: z.number().int().nonnegative(),
  cards_done: z.number().int().nonnegative(),
  cards_in_progress: z.number().int().nonnegative(),
  cards_pause: z.number().int().nonnegative(),
  cards_total: z.number().int().nonnegative(),
  categories: z.number().int().nonnegative(),
})

export const profileStatsDtoSchema = z.object({
  favorite: studyStatsSchema,
  favorite_access: z.object({
    allowed: z.boolean(),
    reason: z
      .string()
      .nullish()
      .transform((value) => value ?? null),
  }),
  myself: studyStatsSchema,
})

export const profileLimitsSchema = z.record(
  z.string(),
  z.number().int().nonnegative(),
)

export type TokenResponse = z.infer<typeof tokenResponseSchema>
export type MessageResponse = z.infer<typeof messageResponseSchema>
export type OAuthStartResponse = z.infer<typeof oauthStartResponseSchema>
export type AuthMethod = z.infer<typeof authMethodSchema>
export type AuthMethods = z.infer<typeof authMethodsResponseSchema>
export type CurrentUser = z.infer<typeof currentUserSchema>
export type PublicUser = z.infer<typeof publicUserSchema>
export type ProfileStatsDto = z.infer<typeof profileStatsDtoSchema>
export type ProfileLimits = z.infer<typeof profileLimitsSchema>
export type ThemeMode = 'light' | 'dark'
export type ReviewMode = 'free' | 'scheduled'
